import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../context/AuthContext'
import { getDownloadUrl } from '../../r2Client'
import {
  HardDrive, Download, File, Image, Film, Music,
  Archive, FileText, Search, X, ChevronDown, Maximize2,
} from 'lucide-react'

/* ─── helpers ─── */
const fmt = (bytes) => {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

const tipoIcon = (tipo) => {
  if (!tipo) return <File size={18} className="text-gray-500" />
  if (tipo.startsWith('image/')) return <Image size={18} className="text-violet-400" />
  if (tipo.startsWith('video/')) return <Film size={18} className="text-blue-400" />
  if (tipo.startsWith('audio/')) return <Music size={18} className="text-emerald-400" />
  if (tipo.includes('pdf') || tipo.includes('word') || tipo.includes('text')) return <FileText size={18} className="text-amber-400" />
  if (tipo.includes('zip') || tipo.includes('rar') || tipo.includes('tar')) return <Archive size={18} className="text-rose-400" />
  return <File size={18} className="text-gray-500" />
}

/* ─── PreviewModal ─── */
const PreviewModal = ({ file, onClose, onDownload }) => {
  const [url, setUrl] = useState(null)

  useEffect(() => {
    if (!file?.r2_key) return
    getDownloadUrl(file.r2_key).then(setUrl)
  }, [file])

  const isImage = file?.tipo?.startsWith('image/')
  const isVideo = file?.tipo?.startsWith('video/')
  const isAudio = file?.tipo?.startsWith('audio/')
  const isPdf   = file?.tipo?.includes('pdf')

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden w-full max-w-5xl flex flex-col"
        style={{ maxHeight: '92vh' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {tipoIcon(file?.tipo)}
            <p className="text-white font-medium truncate text-sm">{file?.nome}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            <button onClick={() => onDownload(file)} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 px-3 py-1.5 rounded-lg transition-colors">
              <Download size={13} /> Scarica
            </button>
            <button onClick={onClose} className="p-1.5 text-gray-500 hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto flex items-center justify-center p-6 min-h-0 bg-gray-950">
          {!url ? (
            <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          ) : isImage ? (
            <img src={url} alt={file.nome} className="max-w-full max-h-full object-contain rounded-xl shadow-2xl" />
          ) : isVideo ? (
            <video src={url} controls autoPlay className="max-w-full max-h-full rounded-xl shadow-2xl" style={{ maxHeight: '75vh' }} />
          ) : isAudio ? (
            <div className="text-center">
              <Music size={48} className="text-emerald-400 mx-auto mb-6" />
              <p className="text-gray-300 text-sm mb-4">{file.nome}</p>
              <audio src={url} controls className="w-80" autoPlay />
            </div>
          ) : isPdf ? (
            <iframe src={url} title={file.nome} className="w-full rounded-xl border border-gray-800" style={{ height: '75vh' }} />
          ) : (
            <div className="text-center py-12">
              <File size={48} className="text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-sm mb-6">Anteprima non disponibile per questo tipo di file.</p>
              <button onClick={() => onDownload(file)} className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition-colors mx-auto">
                <Download size={15} /> Scarica il file
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/* ─── Main ─── */
const ClientFiles = () => {
  const { profile } = useAuth()
  const [files, setFiles] = useState([])
  const [progetti, setProgetti] = useState([])
  const [selectedProject, setSelectedProject] = useState('all')
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [showProjectMenu, setShowProjectMenu] = useState(false)
  const [previewFile, setPreviewFile] = useState(null)

  /* Fetch progetti del cliente */
  useEffect(() => {
    if (!profile?.id) return
    supabase.from('project_members')
      .select('project_id, progetti(id, titolo, colore)')
      .eq('user_id', profile.id)
      .then(({ data }) => {
        setProgetti((data || []).map(d => d.progetti).filter(Boolean))
      })
  }, [profile?.id])

  /* Fetch file */
  const fetchFiles = useCallback(async () => {
    if (!profile?.id) return
    if (progetti.length === 0) { setLoading(false); return }

    const projectIds = progetti.map(p => p.id)
    let q = supabase
      .from('file_uploads')
      .select('*, progetto:project_id(titolo, colore)')
      .is('file_padre_id', null)
      .in('project_id', projectIds)
      .order('created_at', { ascending: false })

    if (selectedProject !== 'all') q = q.eq('project_id', selectedProject)

    const { data } = await q
    setFiles(data || [])
    setLoading(false)
  }, [profile?.id, progetti, selectedProject])

  useEffect(() => { fetchFiles() }, [fetchFiles])

  const handleDownload = async (file) => {
    if (!file.r2_key) return
    const url = await getDownloadUrl(file.r2_key)
    const a = document.createElement('a')
    a.href = url
    a.download = file.nome
    a.click()
  }

  const filtered = files.filter(f => {
    const q = query.toLowerCase()
    return f.nome?.toLowerCase().includes(q) || f.progetto?.titolo?.toLowerCase().includes(q)
  })

  const totalSize = files.reduce((acc, f) => acc + (f.dimensione || 0), 0)
  const selectedLabel = selectedProject === 'all'
    ? 'Tutti i progetti'
    : (progetti.find(p => p.id === selectedProject)?.titolo || '—')

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">File condivisi</h1>
        <p className="text-gray-500 text-sm mt-1">
          {files.length} file · {fmt(totalSize)} · condivisi da Plutonic Studio
        </p>
      </div>

      {/* Filtri */}
      <div className="flex gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Cerca file..."
            className="w-full bg-gray-900 border border-gray-800 rounded-xl pl-9 pr-4 py-2.5 text-white placeholder-gray-600 text-sm focus:outline-none focus:border-violet-500 transition-colors"
          />
        </div>
        {progetti.length > 1 && (
          <div className="relative">
            <button
              onClick={() => setShowProjectMenu(v => !v)}
              className="flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-xl px-4 py-2.5 text-sm text-gray-300 hover:border-gray-700 transition-colors whitespace-nowrap"
            >
              {selectedProject !== 'all' && (
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: progetti.find(p => p.id === selectedProject)?.colore || '#6d28d9' }} />
              )}
              {selectedLabel}
              <ChevronDown size={14} className="text-gray-500" />
            </button>
            {showProjectMenu && (
              <div className="absolute right-0 top-full mt-1 bg-gray-900 border border-gray-800 rounded-xl overflow-hidden z-20 min-w-48 shadow-xl">
                {[{ id: 'all', titolo: 'Tutti i progetti', colore: null }, ...progetti].map(p => (
                  <button
                    key={p.id}
                    onClick={() => { setSelectedProject(p.id); setShowProjectMenu(false) }}
                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left transition-colors ${selectedProject === p.id ? 'bg-violet-600/20 text-violet-300' : 'text-gray-300 hover:bg-gray-800'}`}
                  >
                    {p.colore && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.colore }} />}
                    {p.titolo}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-12 text-center">
          <HardDrive size={36} className="text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">
            {query ? 'Nessun file trovato.' : 'Nessun file ancora condiviso con te.'}
          </p>
          <p className="text-gray-600 text-xs mt-1">Il team di Plutonic Studio caricherà qui i file dei tuoi progetti.</p>
        </div>
      ) : (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden divide-y divide-gray-800">
          {filtered.map(f => (
            <div
              key={f.id}
              onClick={() => setPreviewFile(f)}
              className="flex items-center gap-3 px-4 py-3 hover:bg-gray-800/40 transition-colors group cursor-pointer"
            >
              <div className="flex-shrink-0">{tipoIcon(f.tipo)}</div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{f.nome}</p>
                <p className="text-gray-500 text-xs mt-0.5 flex items-center gap-2">
                  <span>{fmt(f.dimensione)}</span>
                  {f.progetto && (
                    <>
                      <span>·</span>
                      <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: f.progetto.colore || '#6d28d9' }} />
                        {f.progetto.titolo}
                      </span>
                    </>
                  )}
                  <span>·</span>
                  <span>{new Date(f.created_at).toLocaleDateString('it-IT')}</span>
                </p>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                <button onClick={() => setPreviewFile(f)} className="p-1.5 rounded-lg text-gray-500 hover:text-violet-400 hover:bg-gray-700 transition-colors" title="Anteprima">
                  <Maximize2 size={15} />
                </button>
                <button onClick={() => handleDownload(f)} className="p-1.5 rounded-lg text-gray-500 hover:text-violet-400 hover:bg-gray-700 transition-colors" title="Scarica">
                  <Download size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {previewFile && (
        <PreviewModal
          file={previewFile}
          onClose={() => setPreviewFile(null)}
          onDownload={handleDownload}
        />
      )}
    </div>
  )
}

export default ClientFiles
